import { Box, Text } from 'mds-web-ui'
import { AnimatePresence, motion } from 'motion/react'
import { transparentize } from 'polished'
import PropTypes from 'prop-types'
import React from 'react'
import { actionListsAreEqual } from '../utils'
import Action from './Action'

const EmptyListInner = React.forwardRef(({ color }, ref) => (
  <Text
    fontSize={1}
    lineHeight={1.2}
    color={transparentize(0.1, color)}
    ref={ref}
    css={{
      overflow: 'hidden',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      flex: 1,
      position: 'absolute',
      // fill the (min 140px) wrapper and centre via flex. Using top/bottom of
      // 100px each collapsed the message to 0 height inside the 140px wrapper,
      // hiding the empty-list message entirely.
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
      textAlign: 'center',
    }}
  >
    <div>You have no Actions on this list yet.</div>
  </Text>
))

const MotionEmptyList = motion.create(EmptyListInner)

const emptyVariants = {
  show: {
    opacity: 1,
    display: 'flex',
    transition: {
      opacity: { delay: 0.25, duration: 0.2 },
    },
  },
  hide: {
    opacity: 0,
    transition: { duration: 0 },
    transitionEnd: { display: 'none' },
  },
}

const EmptyList = (props) => {
  const { pose, ...rest } = props
  const show = pose === 'show'
  return (
    <MotionEmptyList
      {...rest}
      variants={emptyVariants}
      initial="hide"
      animate={show ? 'show' : 'hide'}
    />
  )
}

const propsAreEqual = (prevProps, nextProps) => {
  if (prevProps.color !== nextProps.color) return false

  if (prevProps.showEmptyListMessage !== nextProps.showEmptyListMessage)
    return false

  if (prevProps.newActionVisible !== nextProps.newActionVisible) return false

  if (prevProps.listId !== nextProps.listId) return false
  if (prevProps.scheduleDay !== nextProps.scheduleDay) return false
  if (prevProps.inSchedule !== nextProps.inSchedule) return false

  if (!actionListsAreEqual(prevProps.actions, nextProps.actions)) return false

  return true
}

const ActionsList = React.memo(
  ({
    color,
    actions,
    showEmptyListMessage = true,
    newActionVisible = false,
    listId,
    scheduleDay,
    inSchedule = false,
  }) => {
    const isEmpty = !actions || actions.length === 0

    return (
      <>
        {/* initial={false} so cards don't animate on first paint (page load),
            only when a new action is actually added */}
        <AnimatePresence initial={false}>
          {actions.map((action, index) => (
            <Action
              key={action.identifier}
              id={action.identifier}
              action={action}
              index={index}
              listId={listId}
              scheduleDay={scheduleDay}
              inSchedule={inSchedule}
            />
          ))}
        </AnimatePresence>

        {/* Reserve the empty-list area (and show the empty message) only in a
            list, and only when we're not adding an action. In the schedule this
            block would make every empty day tall; and a per-day "no Actions"
            message doesn't apply there, so empty schedule days stay compact. */}
        {isEmpty && !newActionVisible && !inSchedule ? (
          <Box
            css={{
              minHeight: '140px',
              height: '100%',
              position: 'relative',
              width: '100%',
            }}
          >
            <EmptyList
              color={color}
              pose={showEmptyListMessage ? 'show' : 'hide'}
            />
          </Box>
        ) : (
          <EmptyList color={color} pose="hide" />
        )}
      </>
    )
  },
  propsAreEqual,
)

ActionsList.displayName = 'ActionsList'

ActionsList.propTypes = {
  color: PropTypes.string.isRequired,
  actions: PropTypes.array.isRequired,
  showEmptyListMessage: PropTypes.bool,
  listId: PropTypes.string,
  scheduleDay: PropTypes.string,
  inSchedule: PropTypes.bool,
}

export default ActionsList
